import {
  IAdminGetComplaintCaseResponse,
  IAdminGetComplaintCasesResponse,
  IAdminGetComplaintsRequest,
  IAdminGetComplaintsResponse,
  IAdminPutReviewsRequest,
  IAdminPutReviewsResponse,
  TUid,
} from '@svitlofour/types';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '../client';
import { version } from '../client/constants';

import { EComplaintsTagType } from './types';

// decision — EModerationDecisionFilter з @svitlofour/types >= 1.2.27 (api_admin_get_complaints >= 1.4.0);
// тримаємо локально, щоб адмінка не чекала релізу типів.
export type TDecisionFilter = 'hidden' | 'restored';

export type TGetComplaintCasesRequest = Omit<
  IAdminGetComplaintsRequest,
  'groupBy' | 'reviewUid'
> & { decision?: TDecisionFilter };

export const complaintsApi = createApi({
  reducerPath: 'complaintsApi',
  tagTypes: [EComplaintsTagType.CASES, EComplaintsTagType.CASE],
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getComplaints: build.query<
      IAdminGetComplaintsResponse,
      IAdminGetComplaintsRequest
    >({
      query: (params) => ({
        url: `${version}/complaints`,
        params,
      }),
      // keepUnusedDataFor: 1, // example how manipulate with RTK Query cache(seconds). Default cache is 60 seconds.
      // cacheTime: 300 // example how to set cacheTime, in which will hold saved date.
    }),
    // Черга: одна справа на Review, фільтри й пагінація на сервері.
    getComplaintCases: build.query<
      IAdminGetComplaintCasesResponse,
      TGetComplaintCasesRequest
    >({
      query: ({ reasons, colors, ...params }) => ({
        url: `${version}/complaints`,
        params: {
          ...params,
          groupBy: 'review',
          reasons: reasons?.length ? reasons.join(',') : undefined,
          colors: colors?.length ? colors.join(',') : undefined,
        },
      }),
      providesTags: [EComplaintsTagType.CASES],
    }),
    // Одна справа: усі скарги Review та історія рішень.
    getComplaintCase: build.query<IAdminGetComplaintCaseResponse, TUid>({
      query: (reviewUid) => ({
        url: `${version}/complaints`,
        params: { reviewUid },
      }),
      providesTags: (_, __, reviewUid) => [
        { type: EComplaintsTagType.CASE, id: reviewUid },
      ],
    }),
    // Рішення модератора: той самий PUT /reviews, але з причиною. Сервер закриває
    // відкриті скарги й пише історію, тож черга і справи перезавантажуються.
    decideOnReviews: build.mutation<
      IAdminPutReviewsResponse,
      IAdminPutReviewsRequest
    >({
      query: (body) => ({
        url: `${version}/reviews`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error, { data }) =>
        error
          ? []
          : [
              EComplaintsTagType.CASES,
              ...data.map(({ uid }) => ({
                type: EComplaintsTagType.CASE,
                id: uid,
              })),
            ],
    }),
  }),
});

export const {
  useGetComplaintsQuery,
  useLazyGetComplaintsQuery,
  useGetComplaintCasesQuery,
  useGetComplaintCaseQuery,
  useDecideOnReviewsMutation,
} = complaintsApi;
